import { Directive, ElementRef, HostListener, inject, input, output } from '@angular/core';

/**
 * Writes the bound text to the system clipboard when the host is clicked or activated from the
 * keyboard, then emits `copied`.
 *
 * Hosts that are not already controls — a `<div>`, a `<span>`, a table cell — are given button
 * semantics and a tab stop, so a copy target is never mouse-only. Native controls are left alone:
 * they are already focusable and the browser already synthesises a click from Enter/Space, so
 * handling those keys here as well would copy twice.
 *
 * `navigator.clipboard` only exists in a secure context, so it is feature-detected rather than
 * assumed. When it is missing, or the write is rejected — the document isn't focused, the
 * permission was denied, the tab is in the background — the host's text is selected instead, so
 * the user can finish the copy with Ctrl+C, and `copyFailed` is emitted in place of `copied`.
 * Callers should treat that as a prompt to change what they tell the user, not as a no-op: a copy
 * that quietly fails is indistinguishable from one that worked, which is exactly the failure mode
 * worth avoiding on a control whose only feedback is a confirmation tooltip.
 */
@Directive({
    selector: '[ftCopyToClipboard]',
    host: {
        '[attr.role]': 'hostRole',
        '[attr.tabindex]': 'hostTabIndex'
    }
})
export class FtCopyToClipboardDirective {
    /** Text to place on the clipboard. */
    readonly ftCopyToClipboard = input.required<string>();

    /** The clipboard write resolved; the payload is the text now on the clipboard. */
    readonly copied = output<string>();

    /** The clipboard was unavailable or refused the write. The host's text has been selected as a fallback. */
    readonly copyFailed = output<string>();

    private readonly host: HTMLElement = inject(ElementRef).nativeElement;

    private readonly isNativeControl = this.host.matches('button, a[href], input, select, textarea');

    protected readonly hostRole = this.isNativeControl ? null : 'button';

    protected readonly hostTabIndex = this.isNativeControl || this.host.hasAttribute('tabindex') ? null : '0';

    @HostListener('click')
    protected onClick(): void {
        void this.copy();
    }

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    protected onKeyboardActivate(event: KeyboardEvent): void {
        if (this.isNativeControl) {
            return;
        }
        // Space would otherwise scroll the modal.
        event.preventDefault();
        void this.copy();
    }

    private async copy(): Promise<void> {
        const text = this.ftCopyToClipboard();
        if (!text) {
            return;
        }

        if (this.canWriteToClipboard()) {
            try {
                await navigator.clipboard.writeText(text);
                this.copied.emit(text);
                return;
            } catch {
                // Fall through to the manual-selection fallback below.
            }
        }

        this.selectHostText(text);
        this.copyFailed.emit(text);
    }

    private canWriteToClipboard(): boolean {
        return window.isSecureContext && typeof navigator.clipboard?.writeText === 'function';
    }

    /**
     * Selects the copied text itself rather than the whole host. Copy targets tend to carry sibling
     * UI — the short-link panel holds the very confirmation tooltip this failure raises — and
     * `selectNodeContents` would swallow it, so Ctrl+C would hand back the link with the "press
     * Ctrl+C" instruction glued onto the end. Anchoring the range inside a single text node also
     * pins it: anything rendered afterwards can no longer extend the selection.
     */
    private selectHostText(text: string): void {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const range = document.createRange();
        const match = this.findText(text);
        if (match) {
            range.setStart(match.node, match.index);
            range.setEnd(match.node, match.index + text.length);
        } else {
            range.selectNodeContents(this.host);
        }

        selection.removeAllRanges();
        selection.addRange(range);
    }

    private findText(text: string): { node: Text; index: number } | null {
        const walker = document.createTreeWalker(this.host, NodeFilter.SHOW_TEXT);
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            const index = node.nodeValue?.indexOf(text) ?? -1;
            if (index >= 0) {
                return { node: node as Text, index };
            }
        }
        return null;
    }
}
